import type {
  ICreatePro014CampaignRequest,
  Pro014StepFourItem,
  StoreSelectorValue,
} from "@/@types/campaign";
import type {
  IConflictResolution,
  IValidatePro014ConflictsRequest,
} from "@/@types/campaign-conflict";
import type { IGradeTargetPayload } from "@/@types/campaign-grade";

import { ALL_STORES_CODE } from "../components/edit-campaign/constants";
import type { Pro014CampaignData, Pro014PrevalidationInput } from "../create/types";
import { collectGradeTargets } from "./grade-targets";

type StoreScopePayload = Pick<ICreatePro014CampaignRequest, "storeCode" | "stores">;

const CONFLICT_SENSITIVE_KEYS: Array<keyof Pro014CampaignData> = [
  "items",
  "startDate",
  "endDate",
  "stores",
  "allStores",
  "gradeRestricted",
];

export const pro014StoreScopePayload = (scope: StoreSelectorValue): StoreScopePayload => {
  if (scope.allStores) return { storeCode: ALL_STORES_CODE };
  return { stores: scope.stores };
};

const gradeTargetsPayload = (
  gradeRestricted: boolean,
  items: Pro014StepFourItem[],
): { gradeTargets?: IGradeTargetPayload[] } => {
  if (!gradeRestricted) return {};
  return { gradeTargets: collectGradeTargets(items) };
};

export const buildCreatePro014Payload = (
  data: Pro014CampaignData,
  conflictResolutions: IConflictResolution[],
): ICreatePro014CampaignRequest => ({
  description: data.description,
  startDate: data.startDate,
  endDate: data.endDate,
  ...pro014StoreScopePayload(data),
  takeQuantity: data.takeQuantity,
  payQuantity: data.payQuantity,
  discountValue: data.discountValue,
  discountType: data.discountType,
  paymentMethod: data.paymentMethod,
  baseValue: data.baseValue,
  items: data.items.map((item: Pro014StepFourItem) => item.sku),
  gradeRestricted: data.gradeRestricted,
  ...gradeTargetsPayload(data.gradeRestricted, data.items),
  conflictResolutions,
});

const excludedCampaign = (
  excludeCampaignCode: number | undefined,
): { excludeCampaignCode?: number } => {
  if (excludeCampaignCode === undefined) return {};
  return { excludeCampaignCode };
};

export const buildConflictValidationRequest = (
  input: Pro014PrevalidationInput,
  chunk: Pro014StepFourItem[],
): IValidatePro014ConflictsRequest => ({
  items: chunk.map((item: Pro014StepFourItem) => item.sku),
  startDate: input.startDate,
  endDate: input.endDate,
  ...pro014StoreScopePayload(input.scope),
  gradeRestricted: input.gradeRestricted,
  ...gradeTargetsPayload(input.gradeRestricted, chunk),
  ...excludedCampaign(input.excludeCampaignCode),
});

export const invalidatesConflicts = (changes: Partial<Pro014CampaignData>): boolean =>
  CONFLICT_SENSITIVE_KEYS.some((key: keyof Pro014CampaignData) => key in changes);
